import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { FaPlay } from 'react-icons/fa';
import MatrixRain from './ui/MatrixRain';

const videos = [
  {
    id: 1,
    title: 'I Survived My Own Mind (Lyrics)',
    thumbnail: '/images/i-survived-my-own-mind.jpg',
    youtubeId: 'Ir-Uf279qfc',
    duration: '4:45',
  },
  {
    id: 2,
    title: 'Ana Baby',
    thumbnail: '/images/ana-baby.jpg',
    youtubeId: 'fWpydYlDb5I',
    duration: '3:53',
  },
  {
    id: 3,
    title: 'Rabbit Mode (Lyrics)',
    thumbnail: '/images/rabbit-mode.jpg',
    youtubeId: '06H_wlKq1BM',
    duration: '2:50',
  },
  {
    id: 4,
    title: 'White Rabbit Bangers (Lyrics)',
    thumbnail: '/images/white-rabbit-bangers.jpg',
    youtubeId: 'PLHonxHfxE4',
    duration: '1:14',
  },
];

export default function Videos() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  return (
    <section id="videos" className="relative py-24 md:py-32 bg-cyber-darker overflow-hidden">
      {/* Matrix Rain Background */}
      <MatrixRain opacity={0.08} />
      
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-15" />
      <div 
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-10"
        style={{
          background: 'radial-gradient(ellipse, rgba(236, 72, 153, 0.5) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-orbitron text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-white">VISUAL </span>
            <span className="bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-cyan bg-clip-text text-transparent">
              EXPERIENCE
            </span>
          </h2>
          <p className="text-white/60 font-rajdhani text-lg max-w-2xl mx-auto">
            The Matrix has you... Dive into the visual world of White Rabbit Bangers. 
            Music videos, visualizers, and glimpses behind the curtain.
          </p>
        </motion.div>

        {/* Featured Video */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="glass-card rounded-2xl overflow-hidden p-2">
            <div className="relative aspect-video bg-cyber-dark rounded-xl overflow-hidden">
              {activeVideo ? (
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1`}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  loading="lazy"
                />
              ) : (
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer group/featured"
                  onClick={() => setActiveVideo(videos[0].youtubeId)}
                >
                  {/* YouTube Thumbnail */}
                  <img
                    src={`https://img.youtube.com/vi/${videos[0].youtubeId}/maxresdefault.jpg`}
                    alt={videos[0].title}
                    className="object-cover w-full h-full absolute inset-0"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/30 group-hover/featured:bg-black/50 transition-colors" />
                  {/* Play button */}
                  <motion.div
                    className="relative z-10 w-20 h-20 rounded-full bg-gradient-to-r from-cyber-cyan to-cyber-purple flex items-center justify-center shadow-lg"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      boxShadow: '0 0 40px rgba(0, 212, 255, 0.5)',
                    }}
                  >
                    <FaPlay className="text-white text-2xl ml-1" />
                  </motion.div>
                </div>
              )}

              {/* Scanlines overlay */}
              <div className="absolute inset-0 pointer-events-none opacity-10"
                style={{
                  background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
                }}
              />
            </div>
          </div>
        </motion.div>

        {/* Video Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {videos.map((video, index) => (
            <motion.div
              key={video.id}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + index * 0.1 }}
              onClick={() => setActiveVideo(video.youtubeId)}
            >
              <div className="glass-card rounded-xl overflow-hidden transition-all duration-300 group-hover:border-cyber-cyan/50">
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  {/* YouTube Thumbnail */}
                  <img
                    src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`}
                    alt={video.title}
                    className="object-cover transition-transform duration-300 group-hover:scale-105 w-full h-full absolute inset-0"
                    loading="lazy"
                  />

                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/50 transition-all flex items-center justify-center">
                    <motion.div
                      className="w-12 h-12 rounded-full bg-cyber-cyan/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      whileHover={{ scale: 1.1 }}
                      style={{
                        boxShadow: '0 0 20px rgba(0, 212, 255, 0.6)',
                      }}
                    >
                      <FaPlay className="text-cyber-dark text-lg ml-0.5" />
                    </motion.div>
                  </div>

                  {/* Duration badge */}
                  <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs font-mono text-white">
                    {video.duration}
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h4 className="font-orbitron font-semibold text-sm text-white group-hover:text-cyber-cyan transition-colors line-clamp-2">
                    {video.title}
                  </h4>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* More Videos CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <a
            href="https://www.youtube.com/@WhiteRabbitBangers"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cyber-cyan font-orbitron text-sm hover:text-cyber-purple transition-colors group"
          >
            <span>View all videos on YouTube</span>
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              →
            </motion.span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

